/**
 * AtelierNavBadge.jsx
 *
 * The gold "Atelier" pill that lives in the top nav (AppHeader).
 *
 * Visibility rules:
 *   - Hidden entirely when entitlement.has_access is false.
 *   - Shown as a calm gold pill with the Atelier glyph when the user
 *     has access. Click → /atelier hub.
 *   - When the user still has their FREE credit remaining, a subtle
 *     pulse animation invites them to use it.
 *
 * Reverent design — never feels like a promo. This is a VIP entrance.
 */
import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles } from "lucide-react";
import { useAtelierEntitlement } from "../hooks/useAtelierEntitlement";

export const AtelierNavBadge = () => {
  const navigate = useNavigate();
  const { loading, has_access, free_credit_remaining } = useAtelierEntitlement();

  if (loading || !has_access) return null;

  return (
    <motion.button
      onClick={() => navigate("/atelier")}
      initial={{ opacity: 0, y: -6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className="relative flex items-center gap-1.5 px-3 py-1.5 rounded-full transition-all duration-300 hover:shadow-md"
      style={{
        background: "linear-gradient(135deg, rgba(215,187,122,0.18) 0%, rgba(168,138,76,0.28) 100%)",
        border: "1px solid rgba(168, 138, 76, 0.55)",
        fontFamily: '"Manrope", sans-serif',
        fontSize: "0.62rem",
        fontWeight: 600,
        letterSpacing: "0.22em",
        textTransform: "uppercase",
        color: "#3A2E14",
      }}
      data-testid="atelier-nav-badge"
      aria-label="Open The Reclaim Atelier"
    >
      {free_credit_remaining && (
        <motion.span
          aria-hidden
          className="absolute inset-0 rounded-full pointer-events-none"
          style={{ border: "1px solid rgba(215,187,122,0.6)" }}
          animate={{ scale: [1, 1.18, 1], opacity: [0.7, 0, 0.7] }}
          transition={{ duration: 2.4, repeat: Infinity, ease: "easeOut" }}
        />
      )}
      <Sparkles size={11} strokeWidth={2.4} />
      <span>Atelier</span>
    </motion.button>
  );
};

export default AtelierNavBadge;
